<script lang="ts">
  /**
   * Notion-style hover preview для linked record. Fixed-position панель,
   * прив'язана до rect bubble'а (щоб не обрізалось overflow клітинки таблиці).
   */
  import Icon from '../../components/Icon.svelte';
  import type { IconName } from '../icons';
  import type { RelationCard } from '../data-tables-api';

  let {
    card,
    rect,
    href,
  }: {
    card: RelationCard;
    rect: DOMRect;
    href: string | null;
  } = $props();

  const typeIcon = $derived<IconName>(
    card.type === 'users' ? 'users' : card.type === 'payments' ? 'payments' : 'table',
  );

  const style = $derived.by(() => {
    const W = 264;
    const top = Math.min(rect.bottom + 6, window.innerHeight - 200);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - W - 8));
    return `top:${top}px; left:${left}px; width:${W}px;`;
  });

  const rows = $derived(
    [
      card.email ? { icon: 'email' as IconName, value: card.email } : null,
      card.phone ? { icon: 'phone' as IconName, value: card.phone } : null,
      card.telegram ? { icon: 'send' as IconName, value: card.telegram } : null,
    ].filter((r): r is { icon: IconName; value: string } => r !== null),
  );
</script>

<div
  class="fixed z-[95] rounded-xl border border-[var(--color-border-3)] bg-white shadow-2xl p-3.5"
  style={style}
  role="tooltip"
>
  <div class="flex items-start gap-2.5">
    <span
      class="grid place-items-center w-9 h-9 rounded-full text-white text-xs font-bold flex-none"
      style="background: {avatarBg(card)};"
    >{card.initials}</span>
    <div class="min-w-0 flex-1">
      <div class="text-sm font-semibold truncate">{card.title}</div>
      {#if card.subtitle}
        <div class="text-[11px] text-[var(--color-text-muted)] truncate">{card.subtitle}</div>
      {/if}
    </div>
  </div>

  {#if rows.length > 0}
    <div class="mt-2.5 space-y-1">
      {#each rows as r (r.icon)}
        <div class="flex items-center gap-2 text-[11.5px] text-[var(--color-text)]">
          <span class="inline-flex text-[var(--color-text-muted-2)]"><Icon name={r.icon} size={12} /></span>
          <span class="truncate">{r.value}</span>
        </div>
      {/each}
    </div>
  {/if}

  <div class="mt-3 flex items-center justify-between border-t border-[var(--color-border-3)] pt-2.5">
    <span class="inline-flex items-center gap-1 text-[10.5px] text-[var(--color-text-muted-2)]">
      <Icon name={typeIcon} size={12} /> {card.type}
    </span>
    {#if href}
      <a {href} class="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--color-pu)] hover:underline">
        Відкрити <Icon name="externalLink" size={11} />
      </a>
    {/if}
  </div>
</div>

<script lang="ts" module>
  import type { RelationCard as RC } from '../data-tables-api';
  const COLORS = ['#7c4ddc', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777', '#4f46e5'];
  /** Стабільний колір аватара з id. */
  export function avatarBg(card: RC): string {
    let h = 0;
    for (const ch of card.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return card.deleted ? '#94a3b8' : COLORS[h % COLORS.length]!;
  }
</script>
